<script>
	const _ = require('underscore');
	const Q = require('q');
	const assignmentService = require('services/assignmentService');
	const stringUtilities = require('utilities/stringUtilities');
	const pubsub = require('events/pubsub');
	const utilities = require('utilities');
	const cloneDeep = utilities.cloneDeep;
	const offlineUtilities = require('utilities/offlineUtilities');
	const { updateOfflineWorkspace } = offlineUtilities;
	const logger = require('utilities/logger');

	/**
	 * Student answers questions.
	 */
	const TYPE_MULTIPLE_CHOICE = 0;
	const TYPE_WRITTEN_ANSWER = 1;
	const TYPE_CLICK_TOUCH_WORD = 2;
	const TYPE_WHO_WHAT = 3;
	const TYPE_EXTERNAL_ACTIVITY = 4;
	const TYPE_IMAGE_ANSWER = 5;
	const TYPE_VOCAB_MULTIPLE_CHOICE = 6;
	const TYPE_SPOKEN = 7;
	const TYPE_TRUE_FALSE = 8;

	/**
	 * Returns the answer/score object to update. If the answer need not
	 * be updated, returns falsy. Doing it this way instead because of
	 * quirkiness (perhaps "features") in vue that make it difficult to
	 * use straight bindings to the model. Doing it "straight" doesn't
	 * invoke complex vue machinery and localizes validation in one function.
	 * @param view    This view.
	 * @param qsid  Question set id.
	 * @param question  The question object.
	 * @param options  (optional) For further sub-questions, and multiple choice parameters.
	 * @param text  The text answer, if any.
	 * @private
	 */
	function _getUpdatedAnswer(view, qsid, question, options, text) {
		var sc = view.scoreStateInternal, // view's model for scores/answers
			originalScoreState = view.scoreState, // clone of same model before the potential change
			update;

		switch (question.type) {
			case TYPE_WRITTEN_ANSWER:
				if (!originalScoreState.t && text) {
					update = {
						t: text
					}; // first time question answered
				} else if (originalScoreState.t !== sc.t) {
					update = {
						t: text
					}; // changed answer to question
				}

				return update;
			case TYPE_WHO_WHAT:
				var sqid = options.sqid;
				if (!sc.r) {
					// first answer to main question
					sc.r = {};
					sc.r[sqid] = text;
					update = {
						sqid: sqid,
						t: text
					};
				} else {
					var subQuestionText = originalScoreState.r && originalScoreState.r[sqid];
					if ((subQuestionText && subQuestionText !== text) || (!subQuestionText && text.length > 0)) {
						update = {
							sqid: sqid,
							t: text
						};
						view.scoreStateInternal.r[sqid].t = text;
					}
				}
				return update;
			case TYPE_MULTIPLE_CHOICE:
			case TYPE_VOCAB_MULTIPLE_CHOICE:
			case TYPE_TRUE_FALSE:
			/* fall through */
			case TYPE_IMAGE_ANSWER:
				if (options.answerId !== sc.aid) {
					update = {
						p: view.ws.productId,
						aid: options.answerId
					};

					view.scoreStateInternal.p = update.p;
					view.scoreStateInternal.aid = update.aid;
				}
				return update;
			default:
				logger.error('qtype not handled');
				return false;
		}
	}

	module.exports = {
		props: {
			'show-as-inactive': {
				required: false,
				type: Boolean,
				default: false
			},

			'show-as-invalid': {
				required: false,
				type: Boolean,
				default: false
			},

			'disable-input': {
				required: false,
				type: Boolean,
				default: false
			},

			grade: {
				required: false
			},

			// assignment id
			aid: {
				required: true
			},

			// question set id
			qsid: {
				required: true
			},

			// question object
			q: {
				required: true
			},

			// score state (optional, if omitted answering is disabled)
			'score-state': {
				required: false
			},

			// workspace
			ws: {
				required: true
			},

			// question number (for visuals)
			qnumb: {
				required: true
			},

			showTts: {
				default: false,
				type: Boolean
			},

			// promise to bypass studentAnswerQuestion
			answerHandler: {
				required: false
			}
		},
		components: {
			'text-to-speech-player-simple': require('components/text-to-speech-player-simple.vue').default,
			'question-text': require('components/question-text.vue').default,
			'validated-textarea': require('components/validated-textarea.vue').default
		},
		modelData: ['offlineMode', 'offlineAssignments', 'disableCopyPaste'],
		data: function () {
			return {
				scoreStateInternal: {},

				TYPE_MULTIPLE_CHOICE,
				TYPE_WRITTEN_ANSWER,
				TYPE_CLICK_TOUCH_WORD,
				TYPE_WHO_WHAT,
				TYPE_EXTERNAL_ACTIVITY,
				TYPE_IMAGE_ANSWER,
				TYPE_VOCAB_MULTIPLE_CHOICE,
				TYPE_SPOKEN,
				TYPE_TRUE_FALSE,
				// convenience variables to show the first question answered for TYPE_WHO_WHAT
				firstSubQuestion: '',
				firstSubAnswer: '',

				// The UI bound version of scoreState. Since scoreState may be omitted, we bind to
				// this and then push to score state and handle answers only if we have a scoreState
				answerModel: {},

				// vars to reference indexes in v-model
				qid: 0,
				subQuestions: {}
			};
		},
		created() {
			this.$watch(
				'q',
				q => {
					this.qid = q.qid;
					var subqs = {};

					// Can't bind to undefined property. previously ignored through hack in _getUpdatedAnswer
					if (q && q.type === TYPE_WRITTEN_ANSWER && this.scoreStateInternal && !this.scoreStateInternal.t) {
						this.scoreStateInternal.t = '';
					}

					if (q.type !== TYPE_WHO_WHAT || q.subQuestions.length === 0) {
						this.firstSubQuestion = '';
						this.firstSubAnswer = '';
					} else {
						var question = q.subQuestions[0].question;

						var m = question.match(/^(.*?>)?([^<>]+\?)([^<>]+)?(<.*)?$/);
						if (m) {
							this.firstSubQuestion = (m[1] || '') + (m[2] || '') + (m[4] || '');
							this.firstSubAnswer = (m[1] || '') + (m[3] || '') + (m[4] || '');
						} else {
							this.firstSubQuestion = '';
							this.firstSubAnswer = '';
						}

						_.each(q.subQuestions, function (sq) {
							subqs[sq.qid] = sq;
						});
					}
					this.subQuestions = subqs;
				},
				{
					deep: true,
					immediate: true
				}
			);
		},
		watch: {
			scoreState: {
				handler(state) {
					if (state) {
						this.scoreStateInternal = cloneDeep(state);
					}
				},
				immediate: true
			}
		},
		methods: {
			handleAnswer(options, event) {
				if (event) {
					event.preventDefault();
				}
				var aid = this.aid;
				var qsid = this.qsid;
				var question = this.q;
				var field = event ? event.target : null;
				var text = stringUtilities.stripScripts(field && field.value);
				var answer = _getUpdatedAnswer(this, qsid, question, options, text);

				// attempt to answer a submitted assignment is a no-op
				if (!this.scoreState || this.ws.s === 's') {
					return;
				}

				// attempt to answer with invalid data shouldn't call api
				if (field && field.validity && !field.validity.valid) {
					return;
				}

				if (answer) {
					answer.qsid = qsid;
					answer.qid = question.qid;
					answer.qt = question.type;
					let promise = Q();
					let answerData = {
						assignmentId: aid,
						answer: answer
					};

					if (this.offlineMode) {
						promise = offlineUtilities.studentAnswerOfflineQuestion(answerData);
					} else {
						if (this.answerHandler) {
							promise = this.answerHandler(answerData);
						} else {
							// for WEBUI-1198, trying to figure out why aid and ans are blank
							logger.info('Invoking assignmentService.studentAnswerQuestion', 'assignmentId', JSON.stringify(aid), 'answer', JSON.stringify(answer));
							promise = assignmentService.studentAnswerQuestion(answerData);
						}

						if (this.$data) {
							this.$emit('update:scoreState', this.scoreStateInternal);
						}

						promise = promise.then(() => {
							updateOfflineWorkspace(aid).catch(e => {});
						});
					}

					pubsub.publish('student-answer-question-promise', promise);
				}
			},
			maybeAssignmentSubmitted() {
				var self = this;
				if (self.ws.s === 's') {
					pubsub.publish('message-error', "You can't answer questions because you have already submitted the assignment.");
				}
			},
			makeInteractive(sentence, word) {
				var i = sentence.indexOf(word);
				if (i !== -1) {
					var endspan = '</span>';
					var w = "<span style='cursor:pointer' onclick='alert(\"Correct!\");'>" + word + endspan;
					var startIncorrectSpan = "<span style='cursor:pointer'  onclick='alert(\"Incorrect!\")'>";
					sentence =
						startIncorrectSpan + sentence.substring(0, i) + endspan + endspan + w + startIncorrectSpan + sentence.substring(word.length + i) + endspan;
				}
				return sentence;
			},
			answerLetter(index = 0) {
				if (this.q.type === TYPE_TRUE_FALSE) {
					return 'TF'[index];
				}

				// Capital letters start at 65
				return String.fromCharCode(index + 65);
			},
			getTextFromHTML(html) {
				// Wrap in <span> to allow for special characters in text
				let text = $('<span>' + html + '</span>').text();
				if (text) {
					return text;
				}
				return html;
			},
			getEventData(answerId) {
				let aid = this.aid;
				let qsid = this.qsid;
				let qid = this.q.qid;
				let tab = 'question_set';

				return {
					aid,
					qsid,
					qid,
					tab,
					answerId
				};
			},
			setScoreStateText(event) {
				if (event && event.target) {
					this.scoreStateInternal.t = event.target.value;
				}
			},
			onPaste(event) {
				if (this.disableCopyPaste) {
					event.preventDefault();
				}
			}
		}
	};
</script>
<template>
	<div
		class="question-set-item"
		:class="{ '-inactive': showAsInactive, '-invalid': showAsInvalid }">
		<div
			@click="maybeAssignmentSubmitted()"
			v-if="[TYPE_MULTIPLE_CHOICE, TYPE_VOCAB_MULTIPLE_CHOICE, TYPE_TRUE_FALSE].includes(q.type)"
			:aria-invalid="showAsInvalid ? 'true' : null">
			<div class="question">
				<div class="question-number">
					<i>{{ qnumb }}</i>
				</div>
				<div
					:id="`question-body-${q.qid}`"
					class="question-body">
					<question-text
						:content="q.question"
						:show-tts="showTts"
						:event-data="getEventData()">
					</question-text>
					<div
						v-if="showAsInvalid"
						class="invalid-message">
						Please select an answer
					</div>
				</div>
			</div>
			<div class="answer">
				<ul
					role="radiogroup"
					:aria-labelledby="`question-body-${q.qid}`">
					<li
						v-for="(a, answerIndex) in q.answers"
						:key="a.answerId"
						:class="{ '-results': !!grade }">
						<div
							class="input-radio"
							:class="{
								correct: scoreStateInternal.aid === a.answerId && grade && grade.score === 100,
								incorrect: scoreStateInternal.aid === a.answerId && grade && grade.score === 0
							}">
							<input
								:id="`input-${q.qid}-${answerIndex}`"
								type="radio"
								lazy
								:disabled="disableInput"
								:name="q.qid"
								:checked="scoreStateInternal.aid === a.answerId"
								@change="handleAnswer({ answerId: a.answerId, correct: a.correct })" />
							<label
								:for="`input-${q.qid}-${answerIndex}`"
								:data-letter="answerLetter(answerIndex)">
								<span>
									<span v-html="a.answer"></span>
									<text-to-speech-player-simple
										v-if="showTts"
										:text="getTextFromHTML(a.answer)"
										:event-data="getEventData(a.answerId)">
									</text-to-speech-player-simple>
								</span>
							</label>
						</div>
					</li>
					<li
						v-if="grade && !grade.answered"
						class="-results">
						<label class="incorrect not-answered">Not answered</label>
					</li>
				</ul>
			</div>
		</div>
		<div
			@click="maybeAssignmentSubmitted()"
			v-if="q.type === TYPE_WRITTEN_ANSWER">
			<div
				v-if="q.question"
				class="question">
				<div class="question-number">
					<i>{{ qnumb }}</i>
				</div>
				<div class="question-body">
					<question-text
						:content="q.question"
						:show-tts="showTts"
						:event-data="getEventData()">
					</question-text>
				</div>
			</div>
			<div
				class="answer"
				:class="{
					results: !!grade,
					'-grade-0': grade && grade.score === 0,
					'-grade-25': grade && grade.score === 25,
					'-grade-50': grade && grade.score === 50,
					'-grade-75': grade && grade.score === 75,
					'-grade-100': grade && grade.score === 100
				}">
				<label
					:for="`input-${q.qid}`"
					class="hidden-visually"
					>Answer</label
				>
				<validated-textarea
					:rows="q.rows || 4"
					cols="50"
					:id="`input-${q.qid}`"
					:value="scoreStateInternal.t"
					:readonly="disableInput"
					:max-words="q.maxWords"
					:maximum-character-count="q.maxlength"
					:maxlength="q.maxlength"
					@input.native="setScoreStateText"
					@change.native="handleAnswer(undefined, $event)"
					@paste.native="onPaste"></validated-textarea>
			</div>
		</div>
		<div
			@click="maybeAssignmentSubmitted()"
			v-if="q.type === TYPE_CLICK_TOUCH_WORD">
			<div class="question">
				<div class="question-number">
					<i>{{ qnumb }}</i>
				</div>
				<div
					class="question-body"
					v-html="q.directionStudent"></div>
			</div>
			<div class="answer">
				<div
					class="excerpt"
					v-for="(e, excerptIndex) in q.excerpts"
					:key="e"
					v-html="makeInteractive(e, q.answers[excerptIndex].answer)"></div>
			</div>
		</div>
		<div
			@click="maybeAssignmentSubmitted()"
			v-if="q.type === TYPE_WHO_WHAT">
			<div class="question">
				<div class="question-number">
					<i>{{ qnumb }}</i>
				</div>
				<div
					class="question-body"
					v-html="q.directionStudent"></div>
			</div>
			<div class="answer">
				<blockquote
					class="excerpt"
					v-for="e in q.excerpts"
					:key="e"
					v-html="e"></blockquote>
			</div>
			<div
				class="sub-question"
				v-for="(sq, sqIndex) in subQuestions"
				:key="sq.question">
				<template v-if="sqIndex === 0">
					<div
						class="question"
						v-html="firstSubQuestion"></div>
					<div
						class="answer"
						v-if="firstSubAnswer">
						<strong>Answer:</strong> <span v-html="firstSubAnswer"></span>
					</div>
				</template>
				<template v-if="sqIndex !== 0">
					<div
						class="question"
						v-html="sq.question"></div>
					<div
						class="answer"
						:class="{
							results: !!grade,
							'-grade-0': grade && grade.score === 0,
							'-grade-25': grade && grade.score === 25,
							'-grade-50': grade && grade.score === 50,
							'-grade-75': grade && grade.score === 75,
							'-grade-100': grade && grade.score === 100
						}">
						<label
							:for="`input-${q.qid}-${sq.qid}`"
							class="hidden-visually"
							>Answer</label
						>
						<textarea
							:id="`input-${q.qid}-${sq.qid}`"
							lazy
							v-model="scoreStateInternal.r[sq.qid].t"
							@change="handleAnswer({ sqid: sq.qid }, $event)"
							rows="4"
							:readonly="disableInput"
							cols="50"
							maxlength="750">
						</textarea>
					</div>
				</template>
			</div>
		</div>
		<div
			@click="maybeAssignmentSubmitted()"
			v-if="q.type === TYPE_EXTERNAL_ACTIVITY || q.type === TYPE_SPOKEN">
			<div
				v-if="q.directionStudent"
				class="question">
				<div class="question-number">
					<i>{{ qnumb }}</i>
				</div>
				<div class="question-body">
					<question-text
						:content="q.directionStudent"
						:show-tts="showTts"
						:event-data="getEventData()">
					</question-text>
				</div>
			</div>
			<div
				v-if="q.question"
				:class="{ question: !q.directionStudent }">
				<i v-if="!q.directionStudent">{{ qnumb }}</i>
				<question-text
					:content="q.question"
					:show-tts="showTts"
					:event-data="getEventData()">
				</question-text>
			</div>
			<div
				class="answer"
				:class="{
					results: !!grade,
					'-grade-0': grade && grade.score === 0,
					'-grade-25': grade && grade.score === 25,
					'-grade-50': grade && grade.score === 50,
					'-grade-75': grade && grade.score === 75,
					'-grade-100': grade && grade.score === 100
				}"></div>
			<p
				class="external-activity-note"
				v-if="q.type === TYPE_EXTERNAL_ACTIVITY">
				(drawing should be done outside of the website)
			</p>
			<p
				class="external-activity-note"
				v-if="q.type === TYPE_SPOKEN">
				(students should answer out loud)
			</p>
		</div>
		<div
			@click="maybeAssignmentSubmitted()"
			v-if="q.type === TYPE_IMAGE_ANSWER">
			<div class="question">
				<div class="question-number">
					<i>{{ qnumb }}</i>
				</div>
				<div class="question-body">
					<question-text
						:content="q.question"
						:show-tts="showTts"
						:event-data="getEventData()">
					</question-text>
				</div>
			</div>
			<div class="answer">
				<div class="grid">
					<div
						class="grid-col-6"
						v-for="(a, answerIndex) in q.answers"
						:key="a.answerId">
						<div
							class="input-radio"
							:class="{
								correct: scoreStateInternal.aid === a.answerId && grade && grade.score === 100,
								incorrect: scoreStateInternal.aid === a.answerId && grade && grade.score === 0
							}">
							<input
								type="radio"
								:id="`input-${q.qid}-${answerIndex}`"
								lazy
								:disabled="disableInput"
								:name="q.qid"
								:checked="a.answerId == scoreStateInternal.aid"
								@change="handleAnswer({ answerId: a.answerId, correct: a.correct })" />
							<label :for="`input-${q.qid}-${answerIndex}`">
								<figure>
									<img
										v-for="i in a.images"
										:key="i"
										:src="i"
										:alt="a.caption" />
									<figcaption>
										{{ a.caption }}
										<text-to-speech-player-simple
											v-if="showTts && a.caption"
											:text="getTextFromHTML(a.caption)"
											:event-data="getEventData(a.answerId)">
										</text-to-speech-player-simple>
									</figcaption>
								</figure>
							</label>
						</div>
					</div>
					<template v-if="grade && !grade.answered">
						<label class="incorrect not-answered">Not answered</label>
					</template>
				</div>
			</div>
		</div>
	</div>
</template>
